import { useId, useState, type FormEvent } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { LiksRagRetrievalSettings } from '../../components/liks/LiksRagRetrievalSettings/LiksRagRetrievalSettings';
import { useI18n } from '../../i18n/useI18n';
import {
  clampRagMaxResults,
  clampRagMinScore,
} from '../../utils/ragRetrievalSettings';

type PlatformChatRagSettingsDialogProps = {
  isOpen: boolean;
  maxResults: number;
  minScore: number;
  onSave: (values: { maxResults: number; minScore: number }) => void;
  onCancel: () => void;
};

export function PlatformChatRagSettingsDialog({
  isOpen,
  ...rest
}: PlatformChatRagSettingsDialogProps) {
  if (!isOpen) {
    return null;
  }

  return <PlatformChatRagSettingsDialogContent {...rest} />;
}

type PlatformChatRagSettingsDialogContentProps = Omit<
  PlatformChatRagSettingsDialogProps,
  'isOpen'
>;

function PlatformChatRagSettingsDialogContent({
  maxResults: initialMaxResults,
  minScore: initialMinScore,
  onSave,
  onCancel,
}: PlatformChatRagSettingsDialogContentProps) {
  const { translate } = useI18n();
  const presetsName = useId();
  const [maxResults, setMaxResults] = useState(() =>
    clampRagMaxResults(initialMaxResults),
  );
  const [minScore, setMinScore] = useState(() =>
    clampRagMinScore(initialMinScore),
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({
      maxResults: clampRagMaxResults(maxResults),
      minScore: clampRagMinScore(minScore),
    });
  };

  return (
    <LiksDialog
      isOpen
      as="form"
      size="md"
      title={translate('platformChat.ragSettings.title')}
      closeLabel={translate('platformChat.ragSettings.close')}
      onClose={onCancel}
      onSubmit={handleSubmit}
      footer={
        <>
          <LiksButton variant="secondary" type="button" onClick={onCancel}>
            {translate('platformChat.ragSettings.cancel')}
          </LiksButton>
          <LiksButton variant="primary" type="submit">
            {translate('platformChat.ragSettings.save')}
          </LiksButton>
        </>
      }
    >
      <LiksRagRetrievalSettings
        maxResults={maxResults}
        minScore={minScore}
        onMaxResultsChange={setMaxResults}
        onMinScoreChange={setMinScore}
        presetsName={presetsName}
        presetsLegend={translate('common.ragRetrieval.presetsLabel')}
        presetLabels={{
          focused: translate('common.ragRetrieval.focused'),
          balanced: translate('common.ragRetrieval.balanced'),
          broad: translate('common.ragRetrieval.broad'),
        }}
        maxResultsLabel={translate('platformChat.ragSettings.maxResultsLabel')}
        maxResultsDescription={translate(
          'platformChat.ragSettings.maxResultsDescription',
        )}
        minScoreLabel={translate('platformChat.ragSettings.minScoreLabel')}
        minScoreDescription={translate(
          'platformChat.ragSettings.minScoreDescription',
        )}
      />
    </LiksDialog>
  );
}
